import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { apiRequest, getRefreshToken, refreshSession, setAccessToken, setRefreshToken } from "../api/client";
import type { Admin } from "../api/types";

interface AuthContextValue {
  admin: Admin | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!getRefreshToken()) {
        setLoading(false);
        return;
      }
      try {
        // Shared, de-duplicated refresh: React StrictMode runs this effect
        // twice in development, and rotating refresh tokens can't be used twice.
        if (!(await refreshSession())) throw new Error("Session expired");
        const me = await apiRequest<Admin>("/auth/me");
        setAdmin(me);
      } catch {
        // refreshSession already cleared the tokens if the server rejected them;
        // a network hiccup keeps them so the next reload can try again.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function login(username: string, password: string) {
    const body = await apiRequest<{ accessToken: string; refreshToken: string; admin: Admin }>("/auth/login", {
      method: "POST",
      auth: false,
      body: { username, password },
    });
    setAccessToken(body.accessToken);
    setRefreshToken(body.refreshToken);
    setAdmin(body.admin);
  }

  async function logout() {
    const refreshToken = getRefreshToken();
    setAccessToken(null);
    setRefreshToken(null);
    setAdmin(null);
    if (refreshToken) {
      try {
        await apiRequest("/auth/logout", { method: "POST", auth: false, body: { refreshToken } });
      } catch {
        // best-effort — the tokens are already cleared client-side
      }
    }
  }

  return <AuthContext.Provider value={{ admin, loading, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
